/**
 * MatchMedia utilities for cross-browser compatibility
 * Handles differences between addEventListener and addListener
 */

/**
 * Safely adds a listener to a MediaQueryList object
 * @param {MediaQueryList} mediaQueryList - The MediaQueryList object
 * @param {Function} callback - The callback function
 * @param {string} eventType - The event type (default: 'change')
 * @returns {boolean} - True if listener was added successfully
 */
export const safeAddMediaQueryListener = (mediaQueryList, callback, eventType = 'change') => {
    try {
        if (!mediaQueryList || typeof callback !== 'function') {
            console.warn('Invalid MediaQueryList or callback provided');
            return false;
        }

        // Modern browsers support addEventListener
        if (mediaQueryList.addEventListener) {
            mediaQueryList.addEventListener(eventType, callback);
            return true;
        }
        // Older browsers (Safari < 14) use addListener
        else if (mediaQueryList.addListener) {
            mediaQueryList.addListener(callback);
            return true;
        }
        // Fallback for very old browsers
        else {
            console.warn('MediaQueryList listener not supported in this browser');
            return false;
        }
    } catch (error) {
        console.error('Error adding MediaQueryList listener:', error);
        return false;
    }
};

/**
 * Safely removes a listener from a MediaQueryList object
 * @param {MediaQueryList} mediaQueryList - The MediaQueryList object
 * @param {Function} callback - The callback function
 * @param {string} eventType - The event type (default: 'change')
 * @returns {boolean} - True if listener was removed successfully
 */
export const safeRemoveMediaQueryListener = (mediaQueryList, callback, eventType = 'change') => {
    try {
        if (!mediaQueryList || typeof callback !== 'function') {
            console.warn('Invalid MediaQueryList or callback provided');
            return false;
        }

        // Modern browsers support removeEventListener
        if (mediaQueryList.removeEventListener) {
            mediaQueryList.removeEventListener(eventType, callback);
            return true;
        }
        // Older browsers (Safari < 14) use removeListener
        else if (mediaQueryList.removeListener) {
            mediaQueryList.removeListener(callback);
            return true;
        }
        // Fallback for very old browsers
        else {
            console.warn('MediaQueryList listener removal not supported in this browser');
            return false;
        }
    } catch (error) {
        console.error('Error removing MediaQueryList listener:', error);
        return false;
    }
};

/**
 * Creates a safe MediaQueryList with listener management
 * @param {string} query - The media query string
 * @param {Function} callback - The callback function
 * @param {string} eventType - The event type (default: 'change')
 * @returns {Object} - Object with MediaQueryList and management methods
 */
export const createSafeMediaQuery = (query, callback, eventType = 'change') => {
    let mediaQueryList = null;
    let isListening = false;
    let callbackRef = null;

    try {
        // Check if matchMedia is supported
        if (typeof window.matchMedia !== 'function') {
            console.warn('matchMedia is not supported in this browser');
            return {
                mediaQueryList: null,
                addListener: () => false,
                removeListener: () => false,
                matches: false,
                isSupported: false
            };
        }

        mediaQueryList = window.matchMedia(query);
        callbackRef = callback;

        return {
            mediaQueryList,
            matches: mediaQueryList.matches,
            isSupported: true,
            addListener: () => {
                if (isListening) return false;
                const success = safeAddMediaQueryListener(mediaQueryList, callbackRef, eventType);
                isListening = success;
                return success;
            },
            removeListener: () => {
                if (!isListening) return false;
                const success = safeRemoveMediaQueryListener(mediaQueryList, callbackRef, eventType);
                isListening = success ? false : isListening;
                return success;
            }
        };
    } catch (error) {
        console.error('Error creating MediaQueryList:', error);
        return {
            mediaQueryList: null,
            addListener: () => false,
            removeListener: () => false,
            matches: false,
            isSupported: false
        };
    }
};

/**
 * React hook for safe MediaQueryList handling
 * @param {string} query - The media query string
 * @param {Function} callback - The callback function
 * @param {string} eventType - The event type (default: 'change')
 * @returns {Object} - Hook object with MediaQueryList operations
 */
export const useSafeMediaQuery = (query, callback, eventType = 'change') => {
    const [matches, setMatches] = React.useState(false);
    const [isSupported, setIsSupported] = React.useState(false);
    const mediaQueryRef = React.useRef(null);

    React.useEffect(() => {
        const safeMediaQuery = createSafeMediaQuery(query, (e) => {
            setMatches(e.matches);
            if (callback) callback(e);
        }, eventType);

        setIsSupported(safeMediaQuery.isSupported);
        setMatches(safeMediaQuery.matches);
        mediaQueryRef.current = safeMediaQuery;

        // Add listener
        safeMediaQuery.addListener();

        // Cleanup on unmount
        return () => {
            if (mediaQueryRef.current) {
                mediaQueryRef.current.removeListener();
            }
        };
    }, [query, eventType]);

    return {
        matches,
        isSupported,
        addListener: () => mediaQueryRef.current?.addListener() || false,
        removeListener: () => mediaQueryRef.current?.removeListener() || false
    };
};

/**
 * Gets browser support information for MediaQueryList
 * @returns {Object} - Support information
 */
export const getMediaQuerySupport = () => {
    const support = {
        matchMedia: false,
        addEventListener: false,
        addListener: false,
        removeEventListener: false,
        removeListener: false,
        userAgent: navigator.userAgent
    };

    try {
        if (typeof window.matchMedia === 'function') {
            support.matchMedia = true;
            const testQuery = window.matchMedia('(min-width: 1px)');
            
            support.addEventListener = typeof testQuery.addEventListener === 'function';
            support.addListener = typeof testQuery.addListener === 'function';
            support.removeEventListener = typeof testQuery.removeEventListener === 'function';
            support.removeListener = typeof testQuery.removeListener === 'function';
        }
    } catch (error) {
        console.warn('Error checking MediaQueryList support:', error);
    }

    return support;
};

export default {
    safeAddMediaQueryListener,
    safeRemoveMediaQueryListener,
    createSafeMediaQuery,
    useSafeMediaQuery,
    getMediaQuerySupport
};
